import { getDpRectAir, getDpRoundAir, getVelocityRect, getVelocityRound, getDpByKms } from "../services/calculations";
import { ObjectTypes } from "../shared/enums";
import { a, getDisplayedShapes } from "../shared/globalState/a";
import { Point } from "./Point";
import { Line } from "./shapes/Line";
import { Shape } from "../shared/types";
import { Block } from "./shapes/Block";
import { isAlmostEqual } from "../shared/common";

export var ductOrPipe = {
    pipe: 'pipe',
    duct: 'duct'
}

export class NetworkInfo {
    private _widthMil: number | null = null;
    private _heightMil: number | null = null;
    private _diameterMil: number | null = null;
    private _flowRateCmh: number = 0;
    private _realLenMet: number = 0;

    type: any;
    velocity: number = 0;
    dp: number = 0;
    localResistances: any[] = [];
    kms: number | null = null;
    kvs: number | null = null;
    realLenMet: number = 0;
    branchIds: string[] = []; // Array of branch IDs this element belongs to

    get widthMil() {
        return this._widthMil;
    }
    set widthMil(v) {
        this._privateResetDiameter();
        this._updateCalculations();
        this._widthMil = v;
    }
    get heightMil() {
        return this._heightMil;
    }
    set heightMil(v) {
        this._privateResetDiameter();
        this._updateCalculations();
        this._heightMil = v;
    }
    get diameterMil() {
        return this._diameterMil;
    }
    set diameterMil(v) {
        this._privateResetWidthAndHeight();
        this._updateCalculations();
        this._diameterMil = v;
    }
    get flowRateCmh() {
        return this._flowRateCmh;
    }
    set flowRateCmh(v) {
        
        this._updateCalculations();
        this._flowRateCmh = v;
    }
    setRealLenMet(len: number | null, zlc: number | null): void {
        if (!len || !zlc) return;
        this._realLenMet = (len / 1000) / zlc;
        this._updateCalculations();
    }
    getRealLenMet() {
        return this._realLenMet;
    }


    /**
     * @param {ObjectTypes} netItemType - труба, воздуховод или элемент сети
     * @param {Array || Number} sizeMil - размер, если круглый, то значение, если прямоугольный - то массив
     * @param {Number} flowRateCmh - расход, метры кубические в час
     * @param {Number} len - длина, указывается только для труб (воздуховодов)
     * @param {Number} zlc - текущий масштаб чертежа, указывается только для труб (воздуховодов), нужен для определения реальной длины отрезка
     * @param {Number} kms - коэффициент местного сопротивления, указывается только для элементов сети Block
     * @param {Number} kvs - коэффициент пропускной способности, указывается только для элементов сети Block
     */
    static createEmptyNetInfo(): NetworkInfo {
        const netInfo = new NetworkInfo(null, null, null, null, null, null, null);
        netInfo.branchIds = [];
        return netInfo;
    }
    constructor(netItemType: any, sizeMil: number | [number, number] | null, flowRateCmh: number | null, len: number | null, zlc: number | null, kms: number | null, kvs: number | null) {
        /**
         * Здесь есть нюанс - свойство flowRateCmh и size можно
         * задать до создания линии и соответственно этого объекта,
         * если он создаётся введением значений в поля ObjectProperties,
         * а вот свойство realLenMet - нельзя, так как
         * длина становится известна только когда линия уже начерчена.
         * Соответственно и velocity мы можем узнать заранее,
         * а вот потери по длине - нет
         */

        this.type = netItemType;

        // *** size
        if (Array.isArray(sizeMil)) {
            this._widthMil = sizeMil[0] ?? null;
            this._heightMil = sizeMil[1] ?? null;
        }
        else if (sizeMil !== null && sizeMil !== undefined) {
            this._diameterMil = sizeMil;
        }
        // *** size


        // --- эти значения могут быть известны заранее
        this._flowRateCmh = flowRateCmh ?? 0;
        this.velocity = 0;

        // --- эти значения известны только после создания линии
        this.setRealLenMet(len, zlc);
        this.dp = 0;

        /**
         * Текущий масштаб чертежа для вычисления реальной длины.
         * Эта переменная есть глобально (a.zlc), но глобальные данные
         * по возможности нужно использовать меньше, так как
         * их изменения трудно отследить. Поэтому
         */

        // *** local resistances collection ***
        this.localResistances = [];

        this.kms = kms ?? null;
        this.kvs = kvs ?? null;
        this.branchIds = []; // Initialize empty branch IDs array

        this._updateCalculations();
        this._updateLocalResistances();

    }

    _privateResetDiameter(): void {
        this._diameterMil = null;
    }
    _privateResetWidthAndHeight(): void {
        this._widthMil = null;
        this._heightMil = null;
    }

    _updateCalculations() {

        if (this.type === ObjectTypes.block || this.type === ObjectTypes.elbowBlock) {
            // скорость velocity назначается в компоненте ObjectProperties по присоединённым участкам
            const calculatedDp = getDpByKms(ObjectTypes.air, this.kms ?? 0, this.velocity);
            this.dp = calculatedDp ?? 0;

        }
        else {
            if (this._diameterMil !== null && this._diameterMil !== undefined) {

                this.velocity = getVelocityRound(this._diameterMil, this._flowRateCmh);
                this.dp = getDpRoundAir(this._diameterMil, this._flowRateCmh, this.getRealLenMet());

            }
            else if (this._widthMil !== null && this._heightMil !== null && this._widthMil !== undefined && this._heightMil !== undefined) {
                this.velocity = getVelocityRect(this._widthMil, this._heightMil, this._flowRateCmh);
                this.dp = getDpRectAir(this._widthMil, this._heightMil, this._flowRateCmh, this.getRealLenMet());

            }
            this._updateLocalResistances();

        }

    }
    _updateLocalResistances() {
        // Local resistances are now managed by ElbowBlock instances in the scene
        // This method is kept for backward compatibility
    }



    getClone() {

        let netInfo: NetworkInfo;
        if (this._diameterMil !== null && this._diameterMil !== undefined) {
            netInfo = new NetworkInfo(this.type, this._diameterMil, this._flowRateCmh, this._realLenMet, null, null, null);
            netInfo.localResistances = Array.from(this.localResistances);
            netInfo._realLenMet = this._realLenMet;
            netInfo.branchIds = Array.from(this.branchIds);

        }
        else if (this._widthMil !== null && this._heightMil !== null && this._widthMil !== undefined && this._heightMil !== undefined) {
            netInfo = new NetworkInfo(this.type, [this._widthMil, this._heightMil], this._flowRateCmh, this._realLenMet, null, null, null);
            netInfo.localResistances = Array.from(this.localResistances);
            netInfo._realLenMet = this._realLenMet;
            netInfo.branchIds = Array.from(this.branchIds);

        }
        else {
            // console.error('>> Не указаны ни диаметр, ни ширина/высота');
            netInfo = NetworkInfo.createEmptyNetInfo();
            netInfo.localResistances = Array.from(this.localResistances);
            netInfo._realLenMet = this._realLenMet;
            netInfo.branchIds = Array.from(this.branchIds);
        }


        return netInfo;
    }

    // *** Branch Management Methods ***

    /**
     * Check if this element belongs to any of the specified branches
     * @param branchIds - Array of branch IDs to check
     * @returns true if element belongs to any of the specified branches
     */
    isBelongToBranch(branchIds: string[]): boolean {
        return branchIds.some(branchId => this.branchIds.includes(branchId));
    }

    /**
     * Check if this element belongs to a specific branch
     * @param branchId - Branch ID to check
     * @returns true if element belongs to the specified branch
     */
    belongsToBranch(branchId: string): boolean {
        return this.branchIds.includes(branchId);
    }

    /**
     * Add this element to a branch
     * @param branchId - Branch ID to add
     */
    addToBranch(branchId: string): void {
        if (!this.branchIds.includes(branchId)) {
            this.branchIds.push(branchId);
        }
    }

    /**
     * Remove this element from a branch
     * @param branchId - Branch ID to remove
     */
    removeFromBranch(branchId: string): void {
        const index = this.branchIds.indexOf(branchId);
        if (index > -1) {
            this.branchIds.splice(index, 1);
        }
    }

    /**
     * Check if this element is shared between multiple branches
     * @returns true if element belongs to more than one branch
     */
    isSharedElement(): boolean {
        return this.branchIds.length > 1;
    }

    /**
     * Get the number of branches this element belongs to
     * @returns number of branches
     */
    getBranchCount(): number {
        return this.branchIds.length;
    }

    /**
     * Clear all branch associations
     */
    clearBranches(): void {
        this.branchIds = [];
    }

    /**
     * Calculate cumulative flow rate for shared elements
     * This method should be called with a map of branch flow rates
     * @param branchFlowRates - Map of branch ID to flow rate
     * @returns cumulative flow rate from all branches this element belongs to
     */
    getCumulativeFlowRate(branchFlowRates: Map<string, number>): number {
        let cumulativeFlow = 0;
        
        for (const branchId of this.branchIds) {
            const branchFlow = branchFlowRates.get(branchId);
            if (branchFlow !== undefined) {
                cumulativeFlow += branchFlow;
            }
        }
        
        return cumulativeFlow;
    }

}